import React, { useEffect, useRef, useState } from 'react';
import { useGoogleMaps } from './GoogleMapsProvider';

const PropertyMapDrawing = ({ onAreaCalculated, addressLocation }) => {
  const mapRef = useRef(null);
  const drawingManagerRef = useRef(null);
  const polygonRef = useRef(null);
  const markerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [area, setArea] = useState(0);
  const [coordinates, setCoordinates] = useState([]);

  const { isLoaded } = useGoogleMaps();

  const calculateArea = (polygon) => {
    const path = polygon.getPath();
    const areaInSquareMeters = window.google.maps.geometry.spherical.computeArea(path);
    
    // Convert to square feet (1 m² = 10.764 ft²)
    const areaInSquareFeet = Math.round(areaInSquareMeters * 10.764);
    
    setArea(areaInSquareFeet);

    // Get coordinates
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      coords.push({
        lat: point.lat(),
        lng: point.lng(),
      });
    }
    
    setCoordinates(coords);

    // Callback to parent
    if (onAreaCalculated) {
      onAreaCalculated({
        area: areaInSquareFeet,
        coordinates: coords,
      });
    }
  };

  useEffect(() => {
    if (isLoaded && mapRef.current && !map) {
      // Initialize map centered on Estrie region with wide view
      const googleMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: 45.4042, lng: -71.8929 }, // Sherbrooke
        zoom: 9, // Wide view of entire Estrie region
        mapTypeId: 'hybrid',
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
      });

      setMap(googleMap);

      // Initialize Drawing Manager
      const drawingManager = new window.google.maps.drawing.DrawingManager({
        drawingMode: window.google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
        },
        polygonOptions: {
          fillColor: '#01500A',
          fillOpacity: 0.3,
          strokeColor: '#ECFF25',
          strokeWeight: 3,
          clickable: true,
          editable: true,
          zIndex: 1,
        },
      });

      drawingManager.setMap(googleMap);
      drawingManagerRef.current = drawingManager;

      // Listen for polygon completion
      window.google.maps.event.addListener(
        drawingManager,
        'polygoncomplete',
        function (polygon) {
          // Remove previous polygon if exists
          if (polygonRef.current) {
            polygonRef.current.setMap(null);
          }

          // Remove address marker when user starts drawing
          if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
          }

          polygonRef.current = polygon;
          
          // Calculate area
          calculateArea(polygon);

          // Stop drawing mode
          drawingManager.setDrawingMode(null);

          // Listen for polygon edits
          window.google.maps.event.addListener(polygon.getPath(), 'set_at', () => {
            calculateArea(polygon);
          });

          window.google.maps.event.addListener(polygon.getPath(), 'insert_at', () => {
            calculateArea(polygon);
          });
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map]);

  // Effect to handle address location changes
  useEffect(() => {
    if (map && addressLocation) {
      // Remove existing marker if any
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      // Create new marker at address location
      markerRef.current = new window.google.maps.Marker({
        position: addressLocation,
        map: map,
        title: 'Votre adresse',
        animation: window.google.maps.Animation.DROP,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#01500A',
          fillOpacity: 1,
          strokeColor: '#ECFF25',
          strokeWeight: 3,
        }
      });

      // Center and zoom map on address
      map.setCenter(addressLocation);
      map.setZoom(19); // Street level zoom
    }
  }, [map, addressLocation]);

  const clearDrawing = () => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setArea(0);
    setCoordinates([]);
    if (onAreaCalculated) {
      onAreaCalculated(null);
    }
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(
        window.google.maps.drawing.OverlayType.POLYGON
      );
    }
    // Restore address marker if address was provided
    if (map && addressLocation && !markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: addressLocation,
        map: map,
        title: 'Votre adresse',
        animation: window.google.maps.Animation.DROP,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#01500A',
          fillOpacity: 1,
          strokeColor: '#ECFF25',
          strokeWeight: 3,
        }
      });
    }
  };

  if (!isLoaded) {
    return (
      <div className="map-loading">
        <div className="spinner"></div>
        <p>Chargement de la carte...</p>
      </div>
    );
  }

  return (
    <div className="property-map-container">
      <div className="map-instructions">
        <h4>📏 Dessinez le contour de votre terrain</h4>
        <p>
          {addressLocation 
            ? 'La carte est centrée sur votre adresse. Cliquez pour tracer le périmètre de votre propriété.' 
            : 'Entrez votre adresse ci-dessus pour centrer la carte, puis tracez le périmètre de votre propriété.'}
        </p>
        {area > 0 && (
          <div className="area-display">
            <strong>Superficie calculée:</strong> {area.toLocaleString()} pi²
            <button type="button" onClick={clearDrawing} className="clear-drawing-btn">
              🗑️ Recommencer
            </button>
          </div>
        )}
      </div>
      <div ref={mapRef} className="property-map" />
    </div>
  );
};

export default PropertyMapDrawing;
